import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { apiFetch, getFileUrl } from '../../utils/api';
import { useToast } from '../../context/ToastContext';
import { useAuth } from '../../context/AuthContext';
import { Button, Modal, FormInput, FormSelect } from '../../components/common/Common';
import MediaUploader from '../../components/common/MediaUploader';
import Brochure from '../../components/brochure/Brochure';
import { generateBrochurePDF, slugifyFilename } from '../../components/brochure/generatePDF';
import EntityInfoButton from '../../components/common/EntityInfoButton';
import Header from '../../components/layout/Header';
import {
  ArrowLeft, Edit2, Trash2, Plus, Search, Calendar,
  Star, X, Image as ImageIcon, Share2, Copy, Link, Eye, XCircle,
  Download, MessageCircle
} from 'lucide-react';
import './ViewProject.css';

const TowerIcon = ({ size = 14 }) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.8"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    <path d="M12 2v2" />
    <path d="M8 4h8" />
    <rect x="7" y="4" width="10" height="18" rx="1" />
    <line x1="10" y1="8" x2="10" y2="9.5" />
    <line x1="14" y1="8" x2="14" y2="9.5" />
    <line x1="10" y1="12" x2="10" y2="13.5" />
    <line x1="14" y1="12" x2="14" y2="13.5" />
    <line x1="10" y1="16" x2="10" y2="17.5" />
    <line x1="14" y1="16" x2="14" y2="17.5" />
    <line x1="11" y1="22" x2="13" y2="22" />
  </svg>
);

const TABS = ['Overview', 'Media', 'Towers', 'Units', 'Timeline'];

const STATUS_CLASS_MAP = {
  'pre-launch': 'pre-launch',
  'under-construction': 'under-construction',
  'nearing-completion': 'nearing-completion',
  'ready-to-move': 'ready-to-move',
  'completed': 'completed',
};

const STATUS_LABEL_MAP = {
  'pre-launch': 'Pre-Launch',
  'under-construction': 'Under Construction',
  'nearing-completion': 'Nearing Completion',
  'ready-to-move': 'Ready to Move',
  'completed': 'Completed',
};

const MILESTONE_STATUS_LABEL = {
  'upcoming': 'Upcoming',
  'in-progress': 'In Progress',
  'completed': 'Completed',
  'delayed': 'Delayed',
};

const UNIT_STATUS_MAP = {
  available: { label: 'Available', color: '#22c55e', bg: '#f0fdf4' },
  sold: { label: 'Sold', color: '#ef4444', bg: '#fef2f2' },
  rented: { label: 'Rented', color: '#3b82f6', bg: '#eff6ff' },
  reserved: { label: 'Reserved', color: '#f59e0b', bg: '#fffbeb' },
};

const ViewProject = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const { showToast } = useToast();
  const { hasPermission, isAdmin } = useAuth();
  const canEdit = isAdmin || hasPermission('projects.edit');
  const [project, setProject] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('Overview');

  // Media state
  const [media, setMedia] = useState([]);
  const [lightboxImg, setLightboxImg] = useState(null);
  const [newMediaFiles, setNewMediaFiles] = useState([]);
  const [uploadingMedia, setUploadingMedia] = useState(false);

  // Towers state
  const [towers, setTowers] = useState([]);
  const [showTowerModal, setShowTowerModal] = useState(false);
  const [editTower, setEditTower] = useState(null);
  const [towerForm, setTowerForm] = useState({ name: '', total_floors: '', units_per_floor: '' });
  const [savingTower, setSavingTower] = useState(false);

  // Units state
  const [units, setUnits] = useState([]);
  const [unitSearch, setUnitSearch] = useState('');
  const [unitTowerFilter, setUnitTowerFilter] = useState('');
  const [unitStatusFilter, setUnitStatusFilter] = useState('');
  const [showUnitModal, setShowUnitModal] = useState(false);
  const [editUnit, setEditUnit] = useState(null);
  const [unitForm, setUnitForm] = useState({
    title: '', unit_number: '', tower_id: '', property_type: '', configuration: '',
    bedrooms: '', bathrooms: '', floor: '',
    carpet_area: '', area: '', area_unit: 'sqft',
    price: '', price_unit: 'INR', status: 'available',
  });
  const [savingUnit, setSavingUnit] = useState(false);

  // Timeline state
  const [milestones, setMilestones] = useState([]);
  const [showMilestoneModal, setShowMilestoneModal] = useState(false);
  const [editMilestone, setEditMilestone] = useState(null);
  const [milestoneForm, setMilestoneForm] = useState({ title: '', description: '', target_date: '', status: 'upcoming' });
  const [savingMilestone, setSavingMilestone] = useState(false);

  // Share state
  const [showShareModal, setShowShareModal] = useState(false);
  const [shareData, setShareData] = useState(null);
  const [shareLoading, setShareLoading] = useState(false);
  const [copied, setCopied] = useState(false);
  const [downloadingPDF, setDownloadingPDF] = useState(false);
  const brochureRef = useRef(null);

  const fetchProject = useCallback(async () => {
    try {
      const res = await apiFetch(`projects/${id}`);
      if (res.success && res.data) {
        setProject(res.data);
        if (Array.isArray(res.data.media)) setMedia(res.data.media);
        if (Array.isArray(res.data.towers)) setTowers(res.data.towers);
        if (Array.isArray(res.data.milestones)) setMilestones(res.data.milestones);
      }
    } catch (err) {
      console.error('Fetch project error:', err);
    }
    setLoading(false);
  }, [id]);

  const fetchTowers = useCallback(async () => {
    try {
      const res = await apiFetch(`projects/${id}/towers`);
      if (res.success) setTowers(Array.isArray(res.data) ? res.data : []);
    } catch (err) {}
  }, [id]);

  const fetchUnits = useCallback(async () => {
    try {
      const res = await apiFetch(`properties?project_id=${id}&limit=500`);
      if (res.success) {
        const list = res.data?.properties || (Array.isArray(res.data) ? res.data : []);
        setUnits(list);
      }
    } catch (err) {}
  }, [id]);

  const fetchMilestones = useCallback(async () => {
    try {
      const res = await apiFetch(`projects/${id}/milestones`);
      if (res.success) setMilestones(Array.isArray(res.data) ? res.data : []);
    } catch (err) {}
  }, [id]);

  const fetchMedia = useCallback(async () => {
    try {
      const res = await apiFetch(`projects/${id}/media`);
      if (res.success) setMedia(Array.isArray(res.data) ? res.data : []);
    } catch (err) {}
  }, [id]);

  useEffect(() => { fetchProject(); }, [fetchProject]);

  useEffect(() => {
    if (activeTab === 'Towers') fetchTowers();
    if (activeTab === 'Units') { fetchUnits(); fetchTowers(); }
    if (activeTab === 'Timeline') fetchMilestones();
    if (activeTab === 'Media') fetchMedia();
  }, [activeTab, fetchTowers, fetchUnits, fetchMilestones, fetchMedia]);

  // ── Tower CRUD ──
  const openAddTower = () => {
    setEditTower(null);
    setTowerForm({ name: '', total_floors: '', units_per_floor: '' });
    setShowTowerModal(true);
  };
  const openEditTower = (t) => {
    setEditTower(t);
    setTowerForm({ name: t.name || '', total_floors: t.total_floors ?? '', units_per_floor: t.units_per_floor ?? '' });
    setShowTowerModal(true);
  };
  const saveTower = async () => {
    if (!towerForm.name.trim()) { showToast('Tower name is required', 'error'); return; }
    setSavingTower(true);
    try {
      const payload = {
        ...towerForm,
        total_floors: towerForm.total_floors ? parseInt(towerForm.total_floors) : null,
        units_per_floor: towerForm.units_per_floor ? parseInt(towerForm.units_per_floor) : null,
      };
      const endpoint = editTower ? `projects/${id}/towers/${editTower.tower_id || editTower.id}` : `projects/${id}/towers`;
      const method = editTower ? 'PUT' : 'POST';
      const res = await apiFetch(endpoint, { method, body: JSON.stringify(payload) });
      if (res.success) {
        showToast(editTower ? 'Tower updated!' : 'Tower added!');
        setShowTowerModal(false);
        fetchTowers();
      } else {
        showToast(res.message || 'Failed', 'error');
      }
    } catch (err) {
      showToast('Failed to save tower', 'error');
    }
    setSavingTower(false);
  };
  const deleteTower = async (towerId) => {
    if (!window.confirm('Delete this tower?')) return;
    try {
      const res = await apiFetch(`projects/${id}/towers/${towerId}`, { method: 'DELETE' });
      if (res.success) { showToast('Tower deleted'); fetchTowers(); }
      else showToast(res.message || 'Failed', 'error');
    } catch (err) { showToast('Failed to delete', 'error'); }
  };

  // ── Media ──
  const handleMediaUpload = async () => {
    if (newMediaFiles.length === 0) return;
    setUploadingMedia(true);
    try {
      const formData = new FormData();
      newMediaFiles.forEach(f => formData.append('media', f));
      const res = await apiFetch(`projects/${id}/media`, { method: 'POST', body: formData });
      if (res.success) {
        showToast('Media uploaded!');
        setNewMediaFiles([]);
        fetchMedia();
      } else {
        showToast(res.message || 'Upload failed', 'error');
      }
    } catch (err) {
      showToast('Upload failed', 'error');
    }
    setUploadingMedia(false);
  };
  const deleteMedia = async (mediaId) => {
    try {
      const res = await apiFetch(`projects/${id}/media/${mediaId}`, { method: 'DELETE' });
      if (res.success) { showToast('Media deleted'); fetchMedia(); }
      else showToast(res.message || 'Failed', 'error');
    } catch (err) { showToast('Failed', 'error'); }
  };
  const setCover = async (mediaId) => {
    try {
      const res = await apiFetch(`projects/${id}/media/${mediaId}/cover`, { method: 'PATCH' });
      if (res.success) { showToast('Cover image set'); fetchMedia(); }
      else showToast(res.message || 'Failed', 'error');
    } catch (err) { showToast('Failed', 'error'); }
  };

  // ── Milestone CRUD ──
  const openAddMilestone = () => {
    setEditMilestone(null);
    setMilestoneForm({ title: '', description: '', target_date: '', status: 'upcoming' });
    setShowMilestoneModal(true);
  };
  const openEditMilestone = (m) => {
    setEditMilestone(m);
    setMilestoneForm({
      title: m.title || m.milestone || '',
      description: m.description || '',
      target_date: m.target_date ? m.target_date.slice(0, 10) : '',
      status: (m.status || 'upcoming').toLowerCase(),
    });
    setShowMilestoneModal(true);
  };
  const saveMilestone = async () => {
    if (!milestoneForm.title.trim()) { showToast('Title is required', 'error'); return; }
    setSavingMilestone(true);
    try {
      const endpoint = editMilestone
        ? `projects/${id}/milestones/${editMilestone.milestone_id || editMilestone.id}`
        : `projects/${id}/milestones`;
      const method = editMilestone ? 'PUT' : 'POST';
      const res = await apiFetch(endpoint, { method, body: JSON.stringify(milestoneForm) });
      if (res.success) {
        showToast(editMilestone ? 'Milestone updated!' : 'Milestone added!');
        setShowMilestoneModal(false);
        fetchMilestones();
      } else {
        showToast(res.message || 'Failed', 'error');
      }
    } catch (err) {
      showToast('Failed to save', 'error');
    }
    setSavingMilestone(false);
  };
  const deleteMilestone = async (msId) => {
    if (!window.confirm('Delete this milestone?')) return;
    try {
      const res = await apiFetch(`projects/${id}/milestones/${msId}`, { method: 'DELETE' });
      if (res.success) { showToast('Milestone deleted'); fetchMilestones(); }
      else showToast(res.message || 'Failed', 'error');
    } catch (err) { showToast('Failed', 'error'); }
  };

  // ── Unit (Property) CRUD ──
  const openAddUnit = () => {
    setEditUnit(null);
    const defaultTowerId = towers.length === 1 ? String(towers[0].tower_id || towers[0].id) : '';
    setUnitForm({
      title: '', unit_number: '', tower_id: defaultTowerId, property_type: '', configuration: '',
      bedrooms: '', bathrooms: '', floor: '',
      carpet_area: '', area: '', area_unit: 'sqft',
      price: '', price_unit: 'INR', status: 'available',
    });
    setShowUnitModal(true);
  };
  const openEditUnit = (u) => {
    setEditUnit(u);
    setUnitForm({
      title: u.title || '',
      unit_number: u.unit_number || '',
      tower_id: u.tower_id ? String(u.tower_id) : '',
      property_type: u.property_type || '',
      configuration: u.configuration || '',
      bedrooms: u.bedrooms ?? '',
      bathrooms: u.bathrooms ?? '',
      floor: u.floor || '',
      carpet_area: u.carpet_area ?? '',
      area: u.area ?? '',
      area_unit: u.area_unit || 'sqft',
      price: u.price ?? '',
      price_unit: u.price_unit || 'INR',
      status: u.status || 'available',
    });
    setShowUnitModal(true);
  };
  const saveUnit = async () => {
    if (!unitForm.title.trim()) { showToast('Title is required', 'error'); return; }
    setSavingUnit(true);
    try {
      const payload = {
        ...unitForm,
        project_id: parseInt(id),
        tower_id: unitForm.tower_id ? parseInt(unitForm.tower_id) : null,
        bedrooms: unitForm.bedrooms === '' ? null : parseInt(unitForm.bedrooms),
        bathrooms: unitForm.bathrooms === '' ? null : parseInt(unitForm.bathrooms),
        carpet_area: unitForm.carpet_area === '' ? null : parseFloat(unitForm.carpet_area),
        area: unitForm.area === '' ? null : parseFloat(unitForm.area),
        price: unitForm.price === '' ? null : parseFloat(unitForm.price),
      };
      const unitId = editUnit?.prop_id || editUnit?.property_id || editUnit?.id;
      const endpoint = editUnit ? `properties/${unitId}` : 'properties';
      const method = editUnit ? 'PUT' : 'POST';
      const res = await apiFetch(endpoint, { method, body: JSON.stringify(payload) });
      if (res.success) {
        showToast(editUnit ? 'Unit updated!' : 'Unit added!');
        setShowUnitModal(false);
        fetchUnits();
      } else {
        showToast(res.message || 'Failed', 'error');
      }
    } catch (err) {
      showToast('Failed to save unit', 'error');
    }
    setSavingUnit(false);
  };
  const deleteUnit = async (unitId) => {
    if (!window.confirm('Delete this unit?')) return;
    try {
      const res = await apiFetch(`properties/${unitId}`, { method: 'DELETE' });
      if (res.success) { showToast('Unit deleted'); fetchUnits(); }
      else showToast(res.message || 'Failed', 'error');
    } catch (err) { showToast('Failed to delete', 'error'); }
  };

  // ── Share ──
  const handleShare = async () => {
    setShareLoading(true);
    setShowShareModal(true);
    setCopied(false);
    try {
      const res = await apiFetch('share', {
        method: 'POST',
        body: JSON.stringify({ entity_type: 'project', entity_id: parseInt(id) }),
      });
      if (res.success && res.data) {
        setShareData(res.data);
      } else {
        showToast(res.message || 'Failed to create share link', 'error');
        setShowShareModal(false);
      }
    } catch (err) {
      showToast('Failed to create share link', 'error');
      setShowShareModal(false);
    }
    setShareLoading(false);
  };

  const copyShareLink = () => {
    if (shareData?.share_url) {
      navigator.clipboard.writeText(shareData.share_url);
      setCopied(true);
      showToast('Link copied to clipboard!');
      setTimeout(() => setCopied(false), 2000);
    }
  };

  const deactivateShareLink = async () => {
    if (!shareData?.link_id) return;
    try {
      const res = await apiFetch(`share/${shareData.link_id}`, { method: 'DELETE' });
      if (res.success) {
        showToast('Share link deactivated');
        setShareData({ ...shareData, is_active: false });
      } else {
        showToast(res.message || 'Failed', 'error');
      }
    } catch (err) {
      showToast('Failed to deactivate', 'error');
    }
  };

  const reactivateShareLink = async (resetCount) => {
    if (!shareData?.link_id) return;
    try {
      const res = await apiFetch(`share/${shareData.link_id}/reactivate`, {
        method: 'PATCH',
        body: JSON.stringify({ reset_count: !!resetCount }),
      });
      if (res.success && res.data) {
        setShareData(res.data);
        showToast(resetCount ? 'Reactivated & view count reset' : 'Link reactivated');
      } else {
        showToast(res.message || 'Failed', 'error');
      }
    } catch (err) {
      showToast('Failed to reactivate', 'error');
    }
  };

  const shareOnWhatsApp = () => {
    if (!shareData?.share_url) return;
    const message = `Check out *${project.name}*${project.developer ? ' by ' + project.developer : ''}\n\n${shareData.share_url}`;
    window.open(`https://wa.me/?text=${encodeURIComponent(message)}`, '_blank', 'noopener,noreferrer');
  };

  const handleDownloadPDF = async () => {
    if (downloadingPDF) return;
    setDownloadingPDF(true);
    try {
      await new Promise(r => setTimeout(r, 100));
      const filename = slugifyFilename(project.name, 'brochure');
      await generateBrochurePDF(brochureRef.current, filename);
      showToast('Brochure downloaded!');
    } catch (err) {
      console.error('PDF generation failed:', err);
      showToast('Failed to generate PDF', 'error');
    }
    setDownloadingPDF(false);
  };

  // ── Filter units ──
  const filteredUnits = units.filter(u => {
    const q = unitSearch.toLowerCase();
    const matchSearch = !q || (u.title || '').toLowerCase().includes(q) || (u.unit_number || '').toLowerCase().includes(q);
    const matchTower = !unitTowerFilter || String(u.tower_id) === unitTowerFilter;
    const matchStatus = !unitStatusFilter || u.status === unitStatusFilter;
    return matchSearch && matchTower && matchStatus;
  });

  if (loading) return <div className="page" style={{ padding: 40, textAlign: 'center', color: '#9ca3af' }}>Loading...</div>;
  if (!project) return <div className="page" style={{ padding: 40, textAlign: 'center', color: '#9ca3af' }}>Project not found</div>;

  const amenities = project.amenities ? project.amenities.split(',').map(a => a.trim()).filter(Boolean) : [];
  const statusKey = (project.construction_status || '').toLowerCase();
  const statusCls = STATUS_CLASS_MAP[statusKey] || '';
  const statusLabel = STATUS_LABEL_MAP[statusKey] || project.construction_status;
  const typeLabel = project.project_type
    ? project.project_type.charAt(0).toUpperCase() + project.project_type.slice(1)
    : '';
  const computedTowerCount = (project.towers || towers).length;
  const computedTotalUnits = (project.towers || towers).reduce(
    (sum, t) => sum + (Number(t.total_floors) || 0) * (Number(t.units_per_floor) || 0),
    0
  );

  return (
    <div className="vp">
      <Header title="Project Details" />
      <div className="page">
        {/* Page Hero */}
        <div className="vp-hero">
          <div className="vp-hero__main">
            <div className="vp-hero__title-row">
              <h1 className="vp-hero__title">{project.name}</h1>
              <EntityInfoButton />
            </div>
            {project.developer && (
              <p className="vp-hero__subtitle">{project.developer}</p>
            )}
            {(project.project_type || project.construction_status) && (
              <div className="vp-hero__badges">
                {project.project_type && (
                  <span className="vp__badge vp__badge--type">{typeLabel}</span>
                )}
                {project.construction_status && (
                  <span className={`vp__badge vp__badge--status-${statusCls}`}>{statusLabel}</span>
                )}
              </div>
            )}
          </div>
          <div className="vp-hero__actions">
            <button
              className="vp-hero__btn vp-hero__btn--ghost"
              onClick={() => navigate('/projects')}
              title="Back to projects"
            >
              <ArrowLeft size={15} /> <span>Back</span>
            </button>
            {canEdit && (
              <button
                className="vp-hero__btn vp-hero__btn--ghost"
                onClick={() => navigate(`/projects/edit/${id}`)}
                title="Edit project"
              >
                <Edit2 size={15} /> <span>Edit</span>
              </button>
            )}
            <button
              className="vp-hero__btn vp-hero__btn--ghost"
              onClick={handleDownloadPDF}
              disabled={downloadingPDF}
              title="Download brochure"
            >
              <Download size={15} /> <span>{downloadingPDF ? 'Generating...' : 'Download'}</span>
            </button>
            <button
              className="vp-hero__btn vp-hero__btn--primary"
              onClick={handleShare}
              title="Share project"
            >
              <Share2 size={15} /> <span>Share</span>
            </button>
          </div>
        </div>

        {/* Tabs */}
        <div className="vp__tabs">
          {TABS.map(tab => (
            <button
              key={tab}
              className={`vp__tab ${activeTab === tab ? 'vp__tab--active' : ''}`}
              onClick={() => setActiveTab(tab)}
            >
              {tab}
            </button>
          ))}
        </div>

        {/* ──────────── Overview ──────────── */}
        {activeTab === 'Overview' && (
          <div className="vp__overview">
            <div>
              {/* Info Grid */}
              <div className="ab-section">
                <h3 className="ab-section__title">Project Information</h3>
                <div className="vp__info-grid">
                  {[
                    { label: 'Developer', value: project.developer || '-' },
                    { label: 'RERA Number', value: project.rera_number || '-' },
                    { label: 'Type', value: typeLabel || '-' },
                    { label: 'Status', value: statusLabel || '-' },
                    { label: 'Possession Date', value: project.possession_date ? new Date(project.possession_date).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }) : '-' },
                    { label: 'Total Towers', value: computedTowerCount || '-' },
                    { label: 'Total Units', value: computedTotalUnits || '-' },
                  ].map((d, i) => (
                    <div className="vp__info-item" key={i}>
                      <span className="vp__info-label">{d.label}</span>
                      <span className="vp__info-value">{d.value}</span>
                    </div>
                  ))}
                </div>
              </div>

              {/* Location */}
              {(project.address || project.city || project.state) && (
                <div className="ab-section">
                  <h3 className="ab-section__title">Location</h3>
                  <div className="vp__info-grid" style={{ gridTemplateColumns: '1fr 1fr' }}>
                    {project.address && (
                      <div className="vp__info-item" style={{ gridColumn: '1/-1' }}>
                        <span className="vp__info-label">Address</span>
                        <span className="vp__info-value">{project.address}</span>
                      </div>
                    )}
                    {project.city && (
                      <div className="vp__info-item">
                        <span className="vp__info-label">City</span>
                        <span className="vp__info-value">{project.city}</span>
                      </div>
                    )}
                    {project.state && (
                      <div className="vp__info-item">
                        <span className="vp__info-label">State</span>
                        <span className="vp__info-value">{project.state}</span>
                      </div>
                    )}
                    {project.pincode && (
                      <div className="vp__info-item">
                        <span className="vp__info-label">Pincode</span>
                        <span className="vp__info-value">{project.pincode}</span>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Description */}
              {project.description && (
                <div className="ab-section">
                  <h3 className="ab-section__title">Description</h3>
                  <p className="vp__description-text">{project.description}</p>
                </div>
              )}
            </div>

            {/* Right sidebar - Amenities */}
            <div>
              {amenities.length > 0 && (
                <div className="ab-section ab-section--sticky">
                  <h3 className="ab-section__title">Amenities</h3>
                  <div className="vp__amenity-chips">
                    {amenities.map((a, i) => (
                      <span key={i} className="vp__amenity-chip">{a}</span>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
        )}

        {/* ──────────── Media ──────────── */}
        {activeTab === 'Media' && (
          <div>
            <div className="vp__media-toolbar">
              <h3 className="ab-section__title" style={{ margin: 0 }}>
                Project Media ({media.length})
              </h3>
            </div>

            {/* Upload section */}
            <div className="ab-section" style={{ marginBottom: 20 }}>
              <MediaUploader
                files={newMediaFiles}
                onFilesChange={setNewMediaFiles}
                accept="image/*,application/pdf,video/*"
                maxFiles={10}
                label="Upload new media"
              />
              {newMediaFiles.length > 0 && (
                <div style={{ marginTop: 12, display: 'flex', justifyContent: 'flex-end' }}>
                  <Button variant="gold" icon={Plus} onClick={handleMediaUpload} disabled={uploadingMedia}>
                    {uploadingMedia ? 'Uploading...' : `Upload ${newMediaFiles.length} file(s)`}
                  </Button>
                </div>
              )}
            </div>

            {/* Gallery */}
            {media.length > 0 ? (
              <div className="vp__media-grid">
                {media.map((m, i) => {
                  const rawUrl = m.url || m.file_url || '';
                  const url = getFileUrl(rawUrl);
                  const mediaType = m.media_type || m.type || '';
                  const isImg = mediaType === 'image' || mediaType === 'floor_plan' || /\.(jpg|jpeg|png|gif|webp|bmp|svg)$/i.test(rawUrl);
                  const isPdf = mediaType === 'brochure' || /\.pdf$/i.test(rawUrl);
                  const isVideo = mediaType === 'video' || /\.(mp4|webm|mov|avi)$/i.test(rawUrl);
                  return (
                    <div className="vp__media-card" key={m.media_id || m.id || i} onClick={() => isImg && setLightboxImg(url)}>
                      {m.is_cover ? <span className="vp__media-cover-badge">Cover</span> : null}
                      {isImg ? (
                        <img className="vp__media-img" src={url} alt={m.name || `Media ${i + 1}`} />
                      ) : isVideo ? (
                        <video className="vp__media-img" src={url} controls={false} muted preload="metadata" style={{ width: '100%', height: 140, objectFit: 'cover' }} />
                      ) : isPdf ? (
                        <a href={url} target="_blank" rel="noopener noreferrer" onClick={e => e.stopPropagation()} style={{ width: '100%', height: 140, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', background: 'var(--gray-50)', color: '#dc2626', textDecoration: 'none', fontSize: 11, fontWeight: 600, gap: 4 }}>
                          <ImageIcon size={28} />
                          <span>PDF — Open</span>
                        </a>
                      ) : (
                        <div style={{ width: '100%', height: 140, display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'var(--gray-50)', color: 'var(--gray-400)' }}>
                          <ImageIcon size={32} />
                        </div>
                      )}
                      <div className="vp__media-actions" onClick={e => e.stopPropagation()}>
                        {isImg && (
                          <button
                            className="vp__media-action-btn vp__media-action-btn--cover"
                            title="Set as cover"
                            onClick={() => setCover(m.media_id || m.id)}
                          >
                            <Star size={12} />
                          </button>
                        )}
                        <button
                          className="vp__media-action-btn vp__media-action-btn--delete"
                          title="Delete"
                          onClick={() => deleteMedia(m.media_id || m.id)}
                        >
                          <Trash2 size={12} />
                        </button>
                      </div>
                      {mediaType && (
                        <span style={{ position: 'absolute', bottom: 4, left: 4, padding: '2px 8px', borderRadius: 6, fontSize: 9.5, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.4, background: 'rgba(0,0,0,0.55)', color: '#fff' }}>
                          {mediaType.replace('_', ' ')}
                        </span>
                      )}
                    </div>
                  );
                })}
              </div>
            ) : (
              <div className="ab-section" style={{ textAlign: 'center', color: 'var(--gray-400)', padding: 40 }}>
                No media uploaded yet
              </div>
            )}

            {/* Lightbox */}
            {lightboxImg && (
              <div className="vp__lightbox" onClick={() => setLightboxImg(null)}>
                <button className="vp__lightbox-close" onClick={() => setLightboxImg(null)}><X size={18} /></button>
                <img className="vp__lightbox-img" src={lightboxImg} alt="Preview" onClick={e => e.stopPropagation()} />
              </div>
            )}
          </div>
        )}

        {/* ──────────── Towers ──────────── */}
        {activeTab === 'Towers' && (
          <div>
            <div className="vp__towers-toolbar">
              <div>
                <h3 className="vp__towers-title">Towers</h3>
                <p className="vp__towers-sub">
                  {towers.length === 0
                    ? 'No towers yet'
                    : `${towers.length} tower${towers.length > 1 ? 's' : ''} · ${towers.reduce((s, t) => s + (Number(t.total_floors) || 0), 0)} total floors`}
                </p>
              </div>
              <Button variant="gold" size="sm" icon={Plus} onClick={openAddTower}>
                Add Tower
              </Button>
            </div>

            {towers.length > 0 ? (
              <div className="vp__towers-grid">
                {towers.map(t => {
                  const tid = t.tower_id || t.id;
                  const floors = Number(t.total_floors) || 0;
                  const perFloor = Number(t.units_per_floor) || 0;
                  const planned = floors * perFloor;
                  const created = Number(t.unit_count) || 0;
                  const totalUnits = planned || created;
                  const fillPct = planned > 0 ? Math.min(100, Math.round((created / planned) * 100)) : 0;
                  return (
                    <div className="vp__tower-card" key={tid}>
                      <div className="vp__tower-cover">
                        <div className="vp__tower-cover-stripes" aria-hidden>
                          {[...Array(Math.min(floors || 6, 10))].map((_, i) => (
                            <span key={i} className="vp__tower-cover-stripe" />
                          ))}
                        </div>
                        <div className="vp__tower-cover-badge">
                          <TowerIcon size={14} />
                          <span>{floors || '—'} {floors === 1 ? 'floor' : 'floors'}</span>
                        </div>
                        <div className="vp__tower-actions">
                          <button className="vp__tower-action" onClick={() => openEditTower(t)} title="Edit">
                            <Edit2 size={13} />
                          </button>
                          <button className="vp__tower-action vp__tower-action--danger" onClick={() => deleteTower(tid)} title="Delete">
                            <Trash2 size={13} />
                          </button>
                        </div>
                      </div>
                      <div className="vp__tower-body">
                        <h4 className="vp__tower-name" title={t.name}>{t.name}</h4>
                        <div className="vp__tower-stats">
                          <div className="vp__tower-stat">
                            <span className="vp__tower-stat-value">{floors || '—'}</span>
                            <span className="vp__tower-stat-label">Floors</span>
                          </div>
                          <div className="vp__tower-stat">
                            <span className="vp__tower-stat-value">{perFloor || '—'}</span>
                            <span className="vp__tower-stat-label">Per Floor</span>
                          </div>
                          <div className="vp__tower-stat">
                            <span className="vp__tower-stat-value">{totalUnits || '—'}</span>
                            <span className="vp__tower-stat-label">Total Units</span>
                          </div>
                        </div>
                        {planned > 0 && (
                          <div className="vp__tower-progress">
                            <div className="vp__tower-progress-meta">
                              <span title="Units added to this tower in the Units tab">
                                Units added · planned {planned}
                              </span>
                              <span><strong>{created}</strong> / {planned}</span>
                            </div>
                            <div className="vp__tower-progress-bar">
                              <span style={{ width: `${fillPct}%` }} />
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            ) : (
              <div className="vp__towers-empty">
                <div className="vp__towers-empty-icon">
                  <TowerIcon size={36} />
                </div>
                <h4>No towers added yet</h4>
                <p>Towers help you organize units by structure. Add your first tower to start tracking inventory.</p>
                <Button variant="gold" icon={Plus} onClick={openAddTower}>Add Tower</Button>
              </div>
            )}

            {/* Tower Modal */}
            <Modal isOpen={showTowerModal} onClose={() => setShowTowerModal(false)} title={editTower ? 'Edit Tower' : 'Add Tower'} size="sm">
              <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
                <FormInput
                  label="Tower Name *"
                  value={towerForm.name}
                  onChange={e => setTowerForm(p => ({ ...p, name: e.target.value }))}
                  placeholder="e.g. Tower A"
                />
                <div className="vp__modal-grid">
                  <FormInput
                    label="Total Floors"
                    type="number"
                    min="0"
                    value={towerForm.total_floors}
                    onChange={e => setTowerForm(p => ({ ...p, total_floors: e.target.value }))}
                    placeholder="0"
                  />
                  <FormInput
                    label="Units per Floor"
                    type="number"
                    min="0"
                    value={towerForm.units_per_floor}
                    onChange={e => setTowerForm(p => ({ ...p, units_per_floor: e.target.value }))}
                    placeholder="0"
                  />
                </div>
              </div>
              <div className="modal__actions">
                <Button variant="outline" onClick={() => setShowTowerModal(false)}>Cancel</Button>
                <Button variant="gold" onClick={saveTower} disabled={savingTower}>
                  {savingTower ? 'Saving...' : (editTower ? 'Update' : 'Add Tower')}
                </Button>
              </div>
            </Modal>
          </div>
        )}

        {/* ──────────── Units ──────────── */}
        {activeTab === 'Units' && (
          <div>
            <div className="vp__units-toolbar">
              <div className="vp__units-search">
                <Search size={15} />
                <input placeholder="Search units..." value={unitSearch} onChange={e => setUnitSearch(e.target.value)} />
              </div>
              {towers.length > 0 && (
                <select className="vp__units-filter" value={unitTowerFilter} onChange={e => setUnitTowerFilter(e.target.value)}>
                  <option value="">All Towers</option>
                  {towers.map(t => (
                    <option key={t.tower_id || t.id} value={t.tower_id || t.id}>{t.name}</option>
                  ))}
                </select>
              )}
              <select className="vp__units-filter" value={unitStatusFilter} onChange={e => setUnitStatusFilter(e.target.value)}>
                <option value="">All Status</option>
                <option value="available">Available</option>
                <option value="sold">Sold</option>
                <option value="rented">Rented</option>
                <option value="reserved">Reserved</option>
              </select>
              <Button variant="gold" size="sm" icon={Plus} onClick={openAddUnit}>
                Add Unit
              </Button>
            </div>

            <div className="table-wrapper">
              <table className="table">
                <thead>
                  <tr>
                    <th>Title</th>
                    <th>Unit No.</th>
                    <th>Tower</th>
                    <th>Type</th>
                    <th>Config</th>
                    <th>Area</th>
                    <th>Price</th>
                    <th>Status</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredUnits.length === 0 ? (
                    <tr>
                      <td colSpan={9} className="table__empty">No units found for this project</td>
                    </tr>
                  ) : (
                    filteredUnits.map(u => {
                      const st = UNIT_STATUS_MAP[u.status] || UNIT_STATUS_MAP.available;
                      const unitId = u.prop_id || u.property_id || u.id;
                      const towerName = towers.find(t => (t.tower_id || t.id) === u.tower_id)?.name || towers.find(t => (t.tower_id || t.id) === u.tower_id)?.tower_name;
                      return (
                        <tr key={unitId}>
                          <td style={{ fontWeight: 600, color: 'var(--gray-800)' }}>{u.title || '-'}</td>
                          <td>{u.unit_number || '-'}</td>
                          <td>{towerName || <span style={{ color: '#cbd5e1' }}>—</span>}</td>
                          <td>{u.property_type || '-'}</td>
                          <td>{u.configuration || '-'}</td>
                          <td>{u.area ? `${u.area} ${u.area_unit || 'sqft'}` : '-'}</td>
                          <td>{u.price ? `₹${Number(u.price).toLocaleString('en-IN')}` : '-'}</td>
                          <td>
                            <span style={{ display: 'inline-block', padding: '3px 10px', borderRadius: 12, fontSize: 11, fontWeight: 600, color: st.color, background: st.bg }}>
                              {st.label}
                            </span>
                          </td>
                          <td>
                            <div style={{ display: 'flex', gap: 4 }}>
                              <button className="vp__tower-action" onClick={() => navigate(`/properties/view/${unitId}`)} title="View">
                                <Eye size={13} />
                              </button>
                              <button className="vp__tower-action" onClick={() => openEditUnit(u)} title="Edit">
                                <Edit2 size={13} />
                              </button>
                              <button className="vp__tower-action vp__tower-action--danger" onClick={() => deleteUnit(unitId)} title="Delete">
                                <Trash2 size={13} />
                              </button>
                            </div>
                          </td>
                        </tr>
                      );
                    })
                  )}
                </tbody>
              </table>
            </div>

            {/* Add / Edit Unit Modal */}
            <Modal isOpen={showUnitModal} onClose={() => setShowUnitModal(false)} title={editUnit ? 'Edit Unit' : 'Add Unit'} size="lg">
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 }}>
                <FormInput
                  label="Title *"
                  value={unitForm.title}
                  onChange={e => setUnitForm(p => ({ ...p, title: e.target.value }))}
                  placeholder="e.g. 3 BHK Premium Apt"
                />
                <FormInput
                  label="Unit Number"
                  value={unitForm.unit_number}
                  onChange={e => setUnitForm(p => ({ ...p, unit_number: e.target.value }))}
                  placeholder="e.g. A-1204"
                />
                <FormSelect
                  label="Tower"
                  value={unitForm.tower_id}
                  onChange={e => setUnitForm(p => ({ ...p, tower_id: e.target.value }))}
                  options={[
                    { value: '', label: '— Select Tower —' },
                    ...towers.map(t => ({ value: String(t.tower_id || t.id), label: t.name || t.tower_name })),
                  ]}
                />
                <FormInput
                  label="Type"
                  value={unitForm.property_type}
                  onChange={e => setUnitForm(p => ({ ...p, property_type: e.target.value }))}
                  placeholder="Apartment / Villa / Plot"
                />
                <FormInput
                  label="Configuration"
                  value={unitForm.configuration}
                  onChange={e => setUnitForm(p => ({ ...p, configuration: e.target.value }))}
                  placeholder="e.g. 3 BHK"
                />
                <FormInput
                  label="Floor"
                  value={unitForm.floor}
                  onChange={e => setUnitForm(p => ({ ...p, floor: e.target.value }))}
                  placeholder="e.g. 12"
                />
                <FormInput
                  label="Bedrooms"
                  type="number"
                  min="0"
                  value={unitForm.bedrooms}
                  onChange={e => setUnitForm(p => ({ ...p, bedrooms: e.target.value }))}
                />
                <FormInput
                  label="Bathrooms"
                  type="number"
                  min="0"
                  value={unitForm.bathrooms}
                  onChange={e => setUnitForm(p => ({ ...p, bathrooms: e.target.value }))}
                />
                <FormInput
                  label="Carpet Area"
                  type="number"
                  min="0"
                  value={unitForm.carpet_area}
                  onChange={e => setUnitForm(p => ({ ...p, carpet_area: e.target.value }))}
                />
                <FormInput
                  label="Super Built-up Area"
                  type="number"
                  min="0"
                  value={unitForm.area}
                  onChange={e => setUnitForm(p => ({ ...p, area: e.target.value }))}
                />
                <FormSelect
                  label="Area Unit"
                  value={unitForm.area_unit}
                  onChange={e => setUnitForm(p => ({ ...p, area_unit: e.target.value }))}
                  options={[
                    { value: 'sqft', label: 'sqft' },
                    { value: 'sqm', label: 'sqm' },
                    { value: 'sqyd', label: 'sqyd' },
                  ]}
                />
                <FormInput
                  label="Price"
                  type="number"
                  min="0"
                  value={unitForm.price}
                  onChange={e => setUnitForm(p => ({ ...p, price: e.target.value }))}
                  placeholder="e.g. 8500000"
                />
                <FormSelect
                  label="Price Unit"
                  value={unitForm.price_unit}
                  onChange={e => setUnitForm(p => ({ ...p, price_unit: e.target.value }))}
                  options={[
                    { value: 'INR', label: 'INR' },
                    { value: 'USD', label: 'USD' },
                    { value: 'AED', label: 'AED' },
                  ]}
                />
                <FormSelect
                  label="Status"
                  value={unitForm.status}
                  onChange={e => setUnitForm(p => ({ ...p, status: e.target.value }))}
                  options={[
                    { value: 'available', label: 'Available' },
                    { value: 'sold', label: 'Sold' },
                    { value: 'rented', label: 'Rented' },
                    { value: 'reserved', label: 'Reserved' },
                  ]}
                />
              </div>
              <div className="modal__actions">
                <Button variant="outline" onClick={() => setShowUnitModal(false)}>Cancel</Button>
                <Button variant="gold" onClick={saveUnit} disabled={savingUnit}>
                  {savingUnit ? 'Saving...' : (editUnit ? 'Update Unit' : 'Add Unit')}
                </Button>
              </div>
            </Modal>
          </div>
        )}

        {/* ──────────── Timeline ──────────── */}
        {activeTab === 'Timeline' && (
          <div>
            <div className="vp__timeline-toolbar">
              <h3 className="ab-section__title" style={{ margin: 0 }}>
                Project Timeline ({milestones.length})
              </h3>
              <Button variant="gold" size="sm" icon={Plus} onClick={openAddMilestone}>
                Add Milestone
              </Button>
            </div>

            {milestones.length > 0 ? (
              <div className="vp__timeline">
                {milestones.map(m => {
                  const msId = m.milestone_id || m.timeline_id || m.id;
                  const statusKey = (m.status || 'upcoming').toLowerCase().replace(/\s+/g, '-');
                  return (
                    <div className="vp__milestone" key={msId}>
                      <div className={`vp__milestone-dot vp__milestone-dot--${statusKey}`} />
                      <div className="vp__milestone-card">
                        <div className="vp__milestone-header">
                          <span className="vp__milestone-title">{m.title || m.milestone}</span>
                          <div className="vp__milestone-actions">
                            <button className="vp__tower-action" onClick={() => openEditMilestone(m)} title="Edit">
                              <Edit2 size={13} />
                            </button>
                            <button className="vp__tower-action vp__tower-action--danger" onClick={() => deleteMilestone(msId)} title="Delete">
                              <Trash2 size={13} />
                            </button>
                          </div>
                        </div>
                        {m.description && <p className="vp__milestone-desc">{m.description}</p>}
                        <div className="vp__milestone-meta">
                          {m.target_date && (
                            <span className="vp__milestone-date">
                              <Calendar size={11} />
                              {new Date(m.target_date).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })}
                            </span>
                          )}
                          <span className={`vp__milestone-status vp__milestone-status--${statusKey}`}>
                            {MILESTONE_STATUS_LABEL[statusKey] || 'Upcoming'}
                          </span>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            ) : (
              <div className="ab-section" style={{ textAlign: 'center', color: 'var(--gray-400)', padding: 40 }}>
                No milestones added yet
              </div>
            )}

            {/* Milestone Modal */}
            <Modal isOpen={showMilestoneModal} onClose={() => setShowMilestoneModal(false)} title={editMilestone ? 'Edit Milestone' : 'Add Milestone'} size="md">
              <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
                <FormInput
                  label="Title *"
                  value={milestoneForm.title}
                  onChange={e => setMilestoneForm(p => ({ ...p, title: e.target.value }))}
                  placeholder="e.g. Foundation Complete"
                />
                <div className="form-group">
                  <label className="form-label">Description</label>
                  <textarea
                    className="form-input"
                    value={milestoneForm.description}
                    onChange={e => setMilestoneForm(p => ({ ...p, description: e.target.value }))}
                    placeholder="Milestone details..."
                    rows={3}
                    style={{ resize: 'vertical' }}
                  />
                </div>
                <div className="vp__modal-grid">
                  <FormInput
                    label="Target Date"
                    type="date"
                    value={milestoneForm.target_date}
                    onChange={e => setMilestoneForm(p => ({ ...p, target_date: e.target.value }))}
                  />
                  <FormSelect
                    label="Status"
                    value={milestoneForm.status}
                    onChange={e => setMilestoneForm(p => ({ ...p, status: e.target.value }))}
                    options={[
                      { value: 'upcoming', label: 'Upcoming' },
                      { value: 'in-progress', label: 'In Progress' },
                      { value: 'completed', label: 'Completed' },
                      { value: 'delayed', label: 'Delayed' },
                    ]}
                  />
                </div>
              </div>
              <div className="modal__actions">
                <Button variant="outline" onClick={() => setShowMilestoneModal(false)}>Cancel</Button>
                <Button variant="gold" onClick={saveMilestone} disabled={savingMilestone}>
                  {savingMilestone ? 'Saving...' : (editMilestone ? 'Update' : 'Add Milestone')}
                </Button>
              </div>
            </Modal>
          </div>
        )}
      </div>

      {/* Share Modal */}
      <Modal isOpen={showShareModal} onClose={() => setShowShareModal(false)} title="Share Project" size="sm">
        {shareLoading ? (
          <div style={{ textAlign: 'center', padding: 30, color: 'var(--gray-400)' }}>
            Generating share link...
          </div>
        ) : shareData ? (
          shareData.is_active === false ? (
            // ─── Deactivated state: reactivate options ─────────────────────
            <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
              <div style={{
                padding: '14px 16px',
                background: '#fef2f2',
                border: '1px solid #fecaca',
                borderRadius: 8,
                display: 'flex',
                alignItems: 'center',
                gap: 10,
              }}>
                <XCircle size={18} style={{ color: '#dc2626', flexShrink: 0 }} />
                <div>
                  <div style={{ fontSize: 13, fontWeight: 600, color: '#991b1b' }}>This link is deactivated</div>
                  <div style={{ fontSize: 12, color: '#7f1d1d', marginTop: 2 }}>
                    Visitors will see "Link Unavailable". Reactivate to share again.
                  </div>
                </div>
              </div>

              <div style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '10px 14px', background: 'var(--gray-50)', borderRadius: 8, border: '1px solid var(--gray-200)' }}>
                <Eye size={16} style={{ color: 'var(--gray-400)' }} />
                <span style={{ fontSize: 13, color: 'var(--gray-600)' }}>
                  <strong style={{ color: 'var(--gray-800)' }}>{shareData.view_count || 0}</strong> previous views
                </span>
              </div>

              <button
                onClick={() => reactivateShareLink(false)}
                style={{
                  display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
                  padding: '12px 14px', background: '#16a34a', border: 'none',
                  color: '#ffffff', fontSize: 13, fontWeight: 600,
                  cursor: 'pointer', borderRadius: 8,
                }}
              >
                Reactivate (keep {shareData.view_count || 0} views)
              </button>
              <button
                onClick={() => reactivateShareLink(true)}
                style={{
                  display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
                  padding: '12px 14px', background: '#ffffff', border: '1px solid #16a34a',
                  color: '#16a34a', fontSize: 13, fontWeight: 600,
                  cursor: 'pointer', borderRadius: 8,
                }}
              >
                Reactivate &amp; Reset Count to 0
              </button>
            </div>
          ) : (
            // ─── Active state: full share options ──────────────────────────
            <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <Link size={16} style={{ color: 'var(--gray-400)', flexShrink: 0 }} />
                <input
                  readOnly
                  value={shareData.share_url}
                  style={{
                    flex: 1, padding: '10px 12px', borderRadius: 8,
                    border: '1px solid var(--gray-200)', fontSize: 13,
                    background: 'var(--gray-50)', color: 'var(--gray-700)',
                    outline: 'none',
                  }}
                  onClick={e => e.target.select()}
                />
                <Button variant={copied ? 'primary' : 'outline'} icon={Copy} onClick={copyShareLink} style={{ flexShrink: 0 }}>
                  {copied ? 'Copied!' : 'Copy'}
                </Button>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '10px 14px', background: 'var(--gray-50)', borderRadius: 8, border: '1px solid var(--gray-200)' }}>
                <Eye size={16} style={{ color: 'var(--gray-400)' }} />
                <span style={{ fontSize: 13, color: 'var(--gray-600)' }}>
                  <strong style={{ color: 'var(--gray-800)' }}>{shareData.view_count || 0}</strong> views
                </span>
                <span style={{ fontSize: 11, color: 'var(--gray-400)', marginLeft: 'auto' }}>
                  Created {new Date(shareData.created_at).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })}
                </span>
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
                <button
                  onClick={shareOnWhatsApp}
                  style={{
                    display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
                    padding: '10px 14px', background: '#25d366', border: 'none',
                    color: '#ffffff', fontSize: 13, fontWeight: 600,
                    cursor: 'pointer', borderRadius: 8,
                  }}
                >
                  <MessageCircle size={16} /> WhatsApp
                </button>
                <button
                  onClick={handleDownloadPDF}
                  disabled={downloadingPDF}
                  style={{
                    display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
                    padding: '10px 14px',
                    background: downloadingPDF ? 'var(--gray-200)' : '#0f172a',
                    border: 'none', color: '#ffffff', fontSize: 13, fontWeight: 600,
                    cursor: downloadingPDF ? 'wait' : 'pointer', borderRadius: 8,
                  }}
                >
                  <Download size={16} /> {downloadingPDF ? 'Generating...' : 'Download PDF'}
                </button>
              </div>
              <button
                onClick={deactivateShareLink}
                style={{
                  display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6,
                  padding: '8px 0', background: 'none', border: 'none',
                  color: '#ef4444', fontSize: 12, fontWeight: 500,
                  cursor: 'pointer', opacity: 0.8,
                }}
              >
                <XCircle size={14} /> Deactivate Link
              </button>
            </div>
          )
        ) : null}
      </Modal>

      {/* Hidden brochure used to render the downloadable PDF */}
      <Brochure
        ref={brochureRef}
        kind="project"
        entity={{ ...project, media }}
        resolveImage={getFileUrl}
      />
    </div>
  );
};

export default ViewProject;
